import { useCallback } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { useCachedFetch } from './useCachedFetch';

/**
 * Listado de irregularidades ACTIVAS (resuelta=false) del tenant actual, con
 * el código público de la parcela ya joineado. La UI lo agrupa por parcela
 * para mostrar el detalle al hover del banner ⚠ en ExpedientesPage.
 *
 * Mig 0074 garantiza que solo hay 1 fila activa por (parcela, subtipo) — no
 * hay duplicados. Si en el futuro se implementa el flujo de «resolver
 * irregularidad», las filas resueltas (resuelta=true) NO aparecen aquí.
 */
export interface IrregularidadActivaRow {
  id: string;
  parcela_id: string;   // código público (HUM-P0042 / COR-P0099)
  subtipo: string;      // ≤80 chars, texto libre del wizard
  gravedad: 'leve' | 'media' | 'alta' | 'bloqueante';
  descripcion: string | null;
  detectada_por: string | null;
  created_at: string;
}

type IrregularidadRowRaw = {
  id: string;
  subtipo: string;
  gravedad: string;
  descripcion: string | null;
  detectada_por: string | null;
  created_at: string;
  parcela: { parcela_id: string | null } | { parcela_id: string | null }[] | null;
};

export function useIrregularidadesList(): {
  data: IrregularidadActivaRow[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const fetcher = useCallback(async (): Promise<IrregularidadActivaRow[]> => {
    if (!isSupabaseConfigured()) return [];
    const sb = getSupabase();
    const { data: rows, error: err } = await sb
      .from('irregularidades')
      .select('id, subtipo, gravedad, descripcion, detectada_por, created_at, parcela:parcelas(parcela_id)')
      .eq('resuelta', false)
      .order('created_at', { ascending: false });
    if (err) throw err;
    const raw = (rows as unknown as IrregularidadRowRaw[]) ?? [];
    return raw.flatMap<IrregularidadActivaRow>((r) => {
      const par = Array.isArray(r.parcela) ? r.parcela[0] : r.parcela;
      const code = par?.parcela_id;
      if (!code) return [];
      return [{
        id: r.id,
        parcela_id: code,
        subtipo: r.subtipo,
        // El CHECK de la columna garantiza solo 4 valores; el cast es seguro.
        gravedad: r.gravedad as IrregularidadActivaRow['gravedad'],
        descripcion: r.descripcion,
        detectada_por: r.detectada_por,
        created_at: r.created_at,
      }];
    });
  }, []);

  // TTL 30s — alineado con expedientes; las irregularidades cambian cuando
  // un técnico vuelve a guardar el wizard (vía RPC upsert_irregularidades_activas).
  const { data, loading, error, refetch } = useCachedFetch<IrregularidadActivaRow[]>(
    'irregularidades_list',
    fetcher,
    { ttlMs: 30_000 },
  );

  return { data: data ?? [], loading, error, refetch };
}
